<script setup lang="ts">
    import { ref, watch } from 'vue';

    const props = withDefaults(
        defineProps<{
            show: boolean;
            minVisibleMs?: number;
            successDurationMs?: number;
        }>(),
        {
            minVisibleMs: 1200,
            successDurationMs: 1800,
        }
    );

    const visible = ref(false);
    const phase = ref<'saving' | 'success'>('saving');
    let shownAt = 0;
    let successTimeout: ReturnType<typeof setTimeout> | undefined;
    let hideTimeout: ReturnType<typeof setTimeout> | undefined;

    function clearPendingTimeouts() {
        clearTimeout(successTimeout);
        clearTimeout(hideTimeout);
    }

    watch(
        () => props.show,
        (show) => {
            clearPendingTimeouts();

            if (show) {
                shownAt = Date.now();
                phase.value = 'saving';
                visible.value = true;
                return;
            }

            const remaining = props.minVisibleMs - (Date.now() - shownAt);
            successTimeout = setTimeout(
                () => {
                    phase.value = 'success';
                    hideTimeout = setTimeout(() => (visible.value = false), props.successDurationMs);
                },
                remaining > 0 ? remaining : 0
            );
        },
        { immediate: true }
    );
</script>

<template>
    <Teleport to="body">
        <div class="saving-indicator" :class="{ in: visible, 'is-success': phase === 'success' }" role="status" aria-live="polite">
            <span v-if="phase === 'saving'" class="saving-indicator-spinner" aria-hidden="true"></span>
            <svg v-else class="saving-indicator-check" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M5 13l4 4L19 7" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            <span>{{ phase === 'saving' ? $Resources.SavingText : $Resources.SavingDoneText }}</span>
        </div>
    </Teleport>
</template>

<style lang="less">
    .saving-indicator {
        --saving-indicator-color: var(--text-color);
        --saving-indicator-bgcolor: var(--brand-secondary-100);

        background-color: var(--saving-indicator-bgcolor);
        color: var(--saving-indicator-color);
        position: fixed;
        min-width: 220px;
        left: calc(var(--nav-current-width) + var(--ug-spacing-lg));
        bottom: var(--ug-spacing-lg);
        z-index: 9999;
        display: none;
        align-items: center;
        gap: var(--ug-spacing-xs);
        padding: var(--ug-spacing-sm) var(--ug-spacing-lg) var(--ug-spacing-sm) var(--ug-spacing-md);
        border-radius: var(--border-radius);
        box-shadow: var(--shadow-dropdown);
        font-weight: 600;
        transition:
            background-color 300ms ease-in-out,
            color 300ms ease-in-out;
    }

    .saving-indicator.in {
        display: flex;
    }

    .saving-indicator:not(.is-success) {
        background-image: linear-gradient(100deg, var(--saving-indicator-bgcolor) 30%, var(--brand-secondary-200) 50%, var(--saving-indicator-bgcolor) 70%);
        background-size: 250% 100%;
        animation: saving-indicator-shimmer 1600ms ease-in-out infinite;
    }

    @keyframes saving-indicator-shimmer {
        from {
            background-position: 100% 0;
        }
        to {
            background-position: 0 0;
        }
    }

    @media (prefers-reduced-motion: reduce) {
        .saving-indicator:not(.is-success) {
            animation: none;
        }
    }

    .saving-indicator.is-success {
        --saving-indicator-color: var(--bg-color);
        --saving-indicator-bgcolor: var(--brand-success);
    }

    .saving-indicator-spinner {
        display: block;
        height: 1rem;
        width: 1rem;
        flex-shrink: 0;
        border: 2px solid var(--brand-secondary-200);
        border-top-color: var(--brand-primary-500);
        border-radius: 50%;
        animation: c-spinner 800ms linear infinite;
    }

    .saving-indicator-check {
        height: 1rem;
        width: 1rem;
        flex-shrink: 0;
        color: var(--saving-indicator-color);
    }
</style>

